import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Combobox } from "@/components/Combobox";

export const MARCAS_VEHICULO = ["HONDA", "YAMAHA", "SUZUKI", "BMW", "DUCATI", "HARLEY DAVIDSON",
  "KTM", "TRIUMPH", "APRILIA", "BAJAJ", "BENELI", "CFMOTO", "PULSAR", "HUSQVARNA", "KATANA",
  "KAWASAKI", "KYMCO", "FREEDOM", "SYM", "UM", "VESPA", "OTRO"];

export const VEHICULO_VACIO = {
  placa: "", marca: "", modelo_id: "", anio: "", chasis: "",
  cliente_id: "", medir_kilometraje: true, kilometraje: 0,
  contacto_nombre: "", contacto_telefono: "",
};

export const validarVehiculo = (v, placaOriginal) => {
  const e = {};
  const placa = v.placa.trim();
  const cambioPlaca = placaOriginal === undefined || placa !== placaOriginal.trim();
  if (!placa) e.placa = "Ingrese placa";
  else if (cambioPlaca && placa.length !== 6) {
    e.placa = "La placa debe tener 6 caracteres (si la moto no tiene placa, use los últimos 6 del chasis)";
  } else if (!cambioPlaca && placa.length > 6) {
    e.placa = "Máximo 6 caracteres";
  }
  if (!v.marca) e.marca = "Seleccione marca";
  if (!v.modelo_id) e.modelo_id = "Seleccione modelo";
  const anio = parseInt(v.anio, 10);
  if (!/^\d{4}$/.test(String(v.anio)) || anio < 1975) e.anio = "El año es requerido o es inferior a 1975";
  if (!v.cliente_id) e.cliente_id = "Seleccione cliente";
  return e;
};

export const nombreCliente = (c) =>
  [c.nombre, c.primer_apellido, c.segundo_apellido].filter(Boolean).join(" ") + ` — ${c.person_id}`;

const Campo = ({ label, obligatorio, error, testId, children }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-slate-700">
      {label} {obligatorio && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && <p data-testid={`${testId}-error`} className="text-xs text-red-500">{error}</p>}
  </div>
);

export const VehiculoForm = ({ valor, onChange, errores = {}, clientes = [], modelos = [] }) => {
  const set = (campo, v) => onChange({ ...valor, [campo]: v });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Campo label="Placa" obligatorio error={errores.placa} testId="vehiculo-placa">
          <Input data-testid="vehiculo-placa-input" value={valor.placa} maxLength={6}
            onChange={(e) => set("placa", e.target.value.toUpperCase())} placeholder="Placa (máx. 6)" />
        </Campo>
        <Campo label="Marca" obligatorio error={errores.marca} testId="vehiculo-marca">
          <Select value={valor.marca} onValueChange={(v) => set("marca", v)}>
            <SelectTrigger data-testid="vehiculo-marca-select"><SelectValue placeholder="Seleccione Marca" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {MARCAS_VEHICULO.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </Campo>
        <Campo label="Modelo" obligatorio error={errores.modelo_id} testId="vehiculo-modelo">
          <Combobox testId="vehiculo-modelo-combobox" value={valor.modelo_id}
            onChange={(v) => set("modelo_id", v)} placeholder="Seleccione Modelo"
            options={modelos.map((m) => ({ value: m.id, label: m.nombre_mostrar }))} />
        </Campo>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Campo label="Año" obligatorio error={errores.anio} testId="vehiculo-anio">
          <Input data-testid="vehiculo-anio-input" value={valor.anio} inputMode="numeric" maxLength={4}
            onChange={(e) => set("anio", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Ej: 2020" />
        </Campo>
        <Campo label="Chasis" error={errores.chasis} testId="vehiculo-chasis">
          <Input data-testid="vehiculo-chasis-input" value={valor.chasis}
            onChange={(e) => set("chasis", e.target.value)} placeholder="Número de chasis (opcional)" />
        </Campo>
        <Campo label="Cliente" obligatorio error={errores.cliente_id} testId="vehiculo-cliente">
          <Combobox testId="vehiculo-cliente-combobox" value={valor.cliente_id}
            onChange={(v) => set("cliente_id", v)} placeholder="Seleccione Cliente"
            options={clientes.map((c) => ({ value: c.id, label: nombreCliente(c) }))} />
        </Campo>
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-2">Contacto (si el dueño es empresa)</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Campo label="Nombre de contacto" testId="vehiculo-contacto-nombre">
            <Input data-testid="vehiculo-contacto-nombre-input" value={valor.contacto_nombre || ""}
              onChange={(e) => set("contacto_nombre", e.target.value)} placeholder="Nombre de la persona de contacto (opcional)" />
          </Campo>
          <Campo label="Teléfono de contacto" testId="vehiculo-contacto-telefono">
            <Input data-testid="vehiculo-contacto-telefono-input" value={valor.contacto_telefono || ""}
              onChange={(e) => set("contacto_telefono", e.target.value)} placeholder="Teléfono de contacto (opcional)" />
          </Campo>
        </div>
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-2">Otras opciones</p>
        <label className="flex items-center gap-3 text-sm text-slate-600">
          <Switch data-testid="vehiculo-medir-km-switch" checked={valor.medir_kilometraje}
            onCheckedChange={(v) => set("medir_kilometraje", v)} />
          Medir kilometraje
        </label>
      </div>
    </div>
  );
};
